import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const FAQ: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getFAQPageData } = useStructuredData();
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const pageTitle = t('faq.title');
  const pageDescription = i18n.language === 'en'
    ? 'Frequently asked questions about Alerta Legal - legal publication monitoring, identity protection, and privacy. Find answers to common questions about our services.'
    : 'Preguntas frecuentes sobre Alerta Legal - monitoreo de publicaciones legales, protección de identidad y privacidad. Encuentra respuestas a preguntas comunes sobre nuestros servicios.';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}${t('routes.faq')}`;
  const structuredData = getFAQPageData(pageTitle, pageDescription, t('routes.faq'));

  const faqItems = [
    {
      id: 'what-is-alerta-legal',
      question: t('faq.questions.whatIs.question'),
      answer: t('faq.questions.whatIs.answer'),
    },
    {
      id: 'how-does-it-work',
      question: t('faq.questions.howItWorks.question'),
      answer: t('faq.questions.howItWorks.answer'),
    },
    {
      id: 'is-it-free',
      question: t('faq.questions.isFree.question'),
      answer: t('faq.questions.isFree.answer'),
    },
    {
      id: 'what-data-used',
      question: t('faq.questions.whatData.question'),
      answer: t('faq.questions.whatData.answer'),
    },
    {
      id: 'what-happens-when-found',
      question: t('faq.questions.whatHappens.question'),
      answer: t('faq.questions.whatHappens.answer'),
    },
    {
      id: 'can-monitor-others',
      question: t('faq.questions.canMonitorOthers.question'),
      answer: t('faq.questions.canMonitorOthers.answer'),
    },
    {
      id: 'how-to-unsubscribe',
      question: t('faq.questions.howToUnsubscribe.question'),
      answer: t('faq.questions.howToUnsubscribe.answer'),
    },
    {
      id: 'data-security',
      question: t('faq.questions.dataSecurity.question'),
      answer: t('faq.questions.dataSecurity.answer'),
    },
    {
      id: 'false-positives',
      question: t('faq.questions.falsePositives.question'),
      answer: t('faq.questions.falsePositives.answer'),
    },
    {
      id: 'coverage-countries',
      question: t('faq.questions.coverage.question'),
      answer: t('faq.questions.coverage.answer'),
    },
  ];

  return (
    <>
      <SEOErrorBoundary fallbackTitle="FAQ - AlertaLegal" fallbackDescription="Frequently asked questions about Alerta Legal - legal publication monitoring, identity protection, and privacy.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            tabIndex={-1}
            id="main-heading"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            {t('faq.title')}
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 6, 
              textAlign: 'center', 
              color: 'text.secondary',
              fontSize: '1.1rem',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            {t('faq.subtitle')}
          </Typography>

          <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              {faqItems.map((item, index) => (
                <Accordion
                  key={item.id}
                  expanded={expandedPanel === item.id}
                  onChange={handleAccordionChange(item.id)}
                  sx={{
                    mb: index === faqItems.length - 1 ? 0 : 1,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: index === faqItems.length - 1 ? '0' : '0 0 8px 0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: expandedPanel === item.id ? 'primary.main' : 'background.paper',
                      color: expandedPanel === item.id ? 'primary.contrastText' : 'text.primary',
                      borderRadius: expandedPanel === item.id ? '8px 8px 0 0' : '8px',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: expandedPanel === item.id ? 'primary.contrastText' : 'text.secondary',
                      },
                      '&:hover': {
                        backgroundColor: expandedPanel === item.id ? 'primary.main' : 'action.hover',
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500,
                        pr: 2,
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: '0 0 8px 8px',
                      pt: 2,
                      pb: 3,
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              {t('faq.stillHaveQuestions')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('faq.contactUs')} <strong>support@alertalegal.co</strong>
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default FAQ;